import React, { useEffect, useState, useRef } from "react";
import { Select, Avatar, Badge, Input, Checkbox } from "antd";
import { UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Assets } from "../../constants/images";
import { CheckMarkIcon, RoundedStaffIcon } from "../../constants/svg";
import { MdRadioButtonUnchecked } from "react-icons/md";
import { TbCircleCheckFilled } from "react-icons/tb";
import { IoCheckmarkCircle } from "react-icons/io5";
import { Button } from "react-bootstrap";
import { GetMembers } from "../../services/team/member";
import AvatarImage from "./AvatarImage";

const { Option } = Select;

// Define your members here
// const members = [
//   { id: 1, name: "Amber Jones", image: Assets.ProfileAvatarOne },
//   { id: 2, name: "Ashley Smith", image: Assets.ProfileAvatarTwo },
//   { id: 3, name: "Brittany Jensen", image: Assets.ProfileAvatarThree },
//   { id: 4, name: "Chelsea Willson", image: Assets.ProfileAvatarFour },
//   { id: 5, name: "Donna Kerrigan", image: Assets.ProfileAvatar },
//   { id: 6, name: "Elizabeth Porter", image: Assets.ProfileAvatarOne },
//   { id: 7, name: "Gina Moccia", image: Assets.ProfileAvatarTwo },
//   { id: 8, name: "Steven Hansen", image: Assets.ProfileAvatarThree },
//   { id: 9, name: "Amber Jones", image: Assets.ProfileAvatar },
// ];

function CustomMembersSelect(props) {
  const { selectedValue, handleChangeMember, className } = props;

  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const selectRef = useRef(null);

  const handleChange = (value) => {
    handleChangeMember(value);
    setSelectedMembers(value);
  };

  const getMember = async () => {
    const response = await GetMembers({
      is_paginated: 0,
      "order-column": "id",
      order: "desc",
    });
    const { data, status, message } = response.data;
    setMembers(data.users);
  };

  useEffect(() => {
    getMember();
  }, []);

  useEffect(() => {
    setSelectedMembers(selectedValue);
  }, [selectedValue]);

  // Update dropdown position when it opens and handle outside clicks
  useEffect(() => {
    const updatePosition = () => {
      if (selectRef.current && dropdownOpen) {
        const rect = selectRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };

    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const menuItemSelectedIcon = <RoundedStaffIcon />;

  return (
    <div ref={selectRef} style={{ position: "relative" }}>
      <Select
        {...props}
        mode="multiple"
        allowClear
        placeholder="Add Member(s)"
        style={{ alignSelf: "center" }}
        value={selectedMembers}
        onChange={handleChange}
        menuItemSelectedIcon={false}
        maxTagCount={0}
        maxTagPlaceholder={(selectedItems) =>
          `${selectedItems.length} Member(s)`
        }
        showSearch={false}
        open={false} // We'll handle the dropdown manually
        onDropdownVisibleChange={(open) => setDropdownOpen(open)}
        className={`CustomeMemberSelect noLabelSelect ${className}`}
        dropdownClassName={"customMemberOvlay"}
        getPopupContainer={() => document.body} // This won't be used since open={false}
        suffixIcon={
          selectedMembers.length > 0 ? (
            <Avatar.Group>
              {selectedMembers.slice(0, 3).map((memberId) => {
                const member = members.find((m) => m.id == memberId);
                if (member) {
                  return (
                    <AvatarImage
                      name={member.name}
                      src={member.image}
                      placeholder={Assets.ProfilePlaceholder}
                      key={memberId}
                      size={32}
                    />
                  );
                }
              })}
              {selectedMembers.length > 3 && (
                <Badge
                  count={`+${selectedMembers.length - 3}`}
                  style={{
                    backgroundColor: "#fff",
                    color: "#999",
                    boxShadow: "0 0 0 1px #d9d9d9 inset",
                  }}
                />
              )}
            </Avatar.Group>
          ) : (
            <RoundedStaffIcon />
          )
        }
        onClick={() => setDropdownOpen(true)}
      />

      {/* Custom Dropdown */}
      {dropdownOpen && (
        <div
          className="customMemberOvlay"
          style={{
            position: "fixed",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            background: "#fff",
            // border: "1px solid #e9e9e9",
            borderRadius: 8,
            boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
            zIndex: 9999,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "400px",
          }}
        >
          {/* Search */}
          <div className="search-container p-3">
            <Input
              placeholder="Search members..."
              prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
              className="transparent-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
                color: "#000",
              }}
            />
          </div>

          {/* Members List */}
          <div style={{ flex: 1, overflowY: "auto", maxHeight: "300px" }}>
            {members
              .filter((member) =>
                member.name.toLowerCase().includes(searchTerm.toLowerCase()),
              )
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((member) => (
                <Checkbox
                  key={member.id}
                  className="mb-3 px-3"
                  reverse={true}
                  checked={selectedMembers.includes(member.id)}
                  name={`member-${member.id}`}
                  value={member.id}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...selectedMembers, member.id]
                      : selectedMembers.filter((id) => id !== member.id);
                    handleChange(next);
                  }}
                >
                  <div className="d-flex align-items-center">
                    <AvatarImage
                      name={member.name}
                      src={member.image}
                      placeholder={Assets.ProfilePlaceholder}
                      size={30}
                    />
                    <h4
                      className="m-0 ms-2 black truncate"
                      style={{ fontSize: "14px", fontWeight: 400 }}
                    >
                      {member.name}
                    </h4>
                  </div>
                </Checkbox>
              ))}
          </div>

          {/* Done Button */}
          <div className="customMemberBtn">
            <Button
              disabled={!selectedMembers.length}
              onClick={() => setDropdownOpen(false)}
              className="w-100"
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomMembersSelect;
